import { useQuery } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import {
  ActionsDropdown,
  createDeleteAction,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import dialogConfirm from 'components/dialogConfirm'
import { TextPill } from 'components/FbUI/TextPill'
import FbButton from 'components/FbUI/FbButton'
import { OverflownText } from 'components/OverflownText'
import { DateCell } from 'components/Tables/CommonTableCells/DateCell'
import {
  TableLinkStyleProvider,
  useTableLinkVariant,
} from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { Tooltip } from 'components/Tooltip/tooltip'
import { Modal } from 'components/UI/Modal/Modal'
import Checkbox from 'components/Checkbox/checkbox'
import { useEffect, useMemo, useState } from 'react'
import {
  PiArrowsClockwise,
  PiKey,
  PiMagnifyingGlass,
  PiPlus,
  PiWarningCircle,
} from 'react-icons/pi'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { getApiErrorDetail } from 'utils/getApiErrorDetail'
import { getTableQueryKey } from 'utils/getTableQueryKey'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { ConnectionsTable } from './ConnectionsTable'
import { GoogleButton, OutlookButton } from './ProviderButtons'
import { EmailIntegration } from 'models/emailIntegration'

const PAGE_SIZE = 25
const sortableFields = ['email', 'modified', 'created']
const tableKey = 'email-integration-table'

export function EmailIntegrationTable() {
  const { enableCalendarIntegration } = useFeatureFlagService()

  // FF ON: render the grants-based connections table
  if (enableCalendarIntegration) {
    return <ConnectionsTable />
  }

  // FF OFF: render the email-only table
  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <ColumnsStoreProvider tableKey={tableKey}>
          <DataTableProvider tableKey={tableKey}>
            <EmailOnlyTable />
          </DataTableProvider>
        </ColumnsStoreProvider>
      </div>
    </TableLinkStyleProvider>
  )
}

function EmailOnlyTable() {
  const linkVariant = useTableLinkVariant()
  const isPlain = linkVariant === 'plain'
  const api = useApiService()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [selectedEmailID, setSelectedEmailID] = useState<number>()
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false)
  const [isShared, setIsShared] = useState<boolean>(true)
  const [email, setEmail] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()

  const sortParams = useMemo(() => {
    const params: Record<string, string> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const success = urlParams.get('success')
    const error = urlParams.get('error')
    if (success) {
      setShowSuccessModal(true)
      urlParams.delete('success')
      window.history.replaceState({}, '', `${window.location.pathname}`)
    } else if (error) {
      const message = urlParams.get('message')
      if (error === 'insufficient_permissions') {
        toast.error(
          message ||
            'You must allow all requested permissions to integrate your email'
        )
      } else {
        toast.error(
          'There was an issue connecting to your email. Make sure you grant the necessary permissions and try again.'
        )
      }
      urlParams.delete('error')
      urlParams.delete('message')
      window.history.replaceState({}, '', `${window.location.pathname}`)
    }
  }, [])

  function openModal() {
    setShowModal(true)
  }

  function hideModal() {
    setShowModal(false)
    setSelectedEmailID(undefined)
  }

  const [page, setPage] = useState(1)

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey,
    filterParams: sortParams,
    page: page,
  })

  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const [err, data] = await to(
        api.emailIntegration.getEmailIntegrationList(
          {
            ...sortParams,
            limit: PAGE_SIZE,
            page: page,
          },
          signal
        )
      )

      if (err) {
        throw err
      }

      return data
    },
  })

  const filteredResults = useMemo(() => {
    const results = data?.results ?? []
    if (!searchQuery) return results
    const q = searchQuery.toLowerCase()
    return results.filter((row) => row.email?.toLowerCase().includes(q))
  }, [data?.results, searchQuery])

  const onContinueButtonClick = async (provider: string) => {
    const [err, response] = await to(
      api.emailIntegration.getEmailIntegrationAuthUrl({
        provider,
        isShared,
        features: ['email'],
      })
    )
    if (err) {
      toast.error('Error creating email')
      return
    }

    if (response?.auth_url) {
      window.location.href = response.auth_url
    }

    toast.success('Email integration requested')
    await refetch()
    hideModal()
  }

  const onSubmitUpdate = async () => {
    if (!selectedEmailID) return

    const [err] = await to(
      api.emailIntegration.updateEmailIntegration(selectedEmailID, {
        is_shared: isShared,
      })
    )
    if (err) {
      toast.error('Error updating email')
      return
    }

    toast.success('Email updated')
    await refetch()
    hideModal()
  }

  async function handleRowDelete(index: number) {
    const id = filteredResults?.[index]?.id
    if (!id) return

    if (
      await dialogConfirm(
        'Removing this integration will delete any record of existing emails sent or received by this address, and prevent any future inbound or outbound mail from syncing to First Bite.',
        'Remove Email Integration from First Bite?'
      )
    ) {
      const [err] = await to(api.emailIntegration.deleteEmailIntegration(id))
      if (err) {
        toast.error('Error deleting email')
        return
      }
      toast.success('Email deleted')
      await refetch()
    }
  }

  function handleRowEdit(index: number) {
    setShowModal(true)
    const row = filteredResults?.[index]
    if (row) {
      setSelectedEmailID(row.id)
      setIsShared(row.is_shared)
      setEmail(row.email)
    }
  }

  async function syncEmailIntegration(id: number) {
    const [err] = await to(api.emailIntegration.syncEmailIntegration(id))
    if (err) {
      toast.error(getApiErrorDetail(err, 'Error syncing email'))
      return
    }
    toast.info(
      'Started syncing emails for this account. You will be notified when it is done.'
    )
    await refetch()
  }

  async function reauthenticateEmailIntegration(integration: EmailIntegration) {
    if (!integration.provider) {
      toast.error(
        'Unable to reauthenticate: provider information not available'
      )
      return
    }

    const [err, response] = await to(
      api.emailIntegration.getEmailIntegrationAuthUrl({
        provider: integration.provider,
        isShared: integration.is_shared,
        email: integration.email,
        features: ['email'],
      })
    )
    if (err) {
      toast.error('Error creating reauthentication URL')
      return
    }

    if (response?.auth_url) {
      window.location.href = response.auth_url
    }
  }

  const columns = useMemo<ColumnDef<EmailIntegration, any>[]>(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        size: 260,
        meta: isPlain ? { isFirstColumn: true } : undefined,
        cell: (info) => (
          <OverflownText>{info.getValue() as string}</OverflownText>
        ),
      },
      {
        accessorKey: 'is_shared',
        header: 'Shared',
        size: 120,
        cell: ({ row }) => (row.original.is_shared ? 'Yes' : 'No'),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        cell: (info) => {
          const value = info.getValue() as string
          if (value === 'synced') {
            return (
              <TextPill textColor="#15803d" backgroundColor="#dcfce7">
                Synced
              </TextPill>
            )
          }
          if (value === 'syncing') {
            return (
              <TextPill textColor="#a16207" backgroundColor="#fef9c3">
                Syncing
              </TextPill>
            )
          }
          return (
            <TextPill textColor="#b91c1c" backgroundColor="#fee2e2">
              Not Synced
            </TextPill>
          )
        },
      },
      {
        accessorKey: 'is_expired',
        header: 'Connection',
        size: 160,
        cell: (info) => {
          const isExpired = info.getValue() as boolean
          const integration = info.row.original

          if (isExpired) {
            return (
              <Tooltip
                title="Connection broken"
                content="Click to reauthenticate your email account"
                placement="top"
              >
                <button
                  type="button"
                  onClick={() => reauthenticateEmailIntegration(integration)}
                  className="inline-flex items-center gap-1 cursor-pointer"
                >
                  <TextPill textColor="#b91c1c" backgroundColor="#fee2e2">
                    <PiWarningCircle size={12} className="mr-0.5" />
                    Disconnected
                  </TextPill>
                </button>
              </Tooltip>
            )
          }

          return (
            <TextPill textColor="#15803d" backgroundColor="#dcfce7">
              Connected
            </TextPill>
          )
        },
      },
      {
        accessorKey: 'modified',
        header: 'Modified',
        size: 140,
        cell: DateCell,
      },
      {
        accessorKey: 'created',
        header: 'Created',
        size: 140,
        cell: DateCell,
      },
      {
        id: 'actions',
        header: isPlain
          ? () => <div className="w-full text-center">Actions</div>
          : 'Actions',
        size: isPlain ? 70 : 120,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
          rightAlign: !isPlain,
        },
        cell: ({ row }) => {
          const actions = [
            createEditAction(() => handleRowEdit(row.index)),
            {
              label: 'Reauthenticate',
              icon: PiKey,
              onClick: () => reauthenticateEmailIntegration(row.original),
            },
            {
              label: 'Sync emails',
              icon: PiArrowsClockwise,
              disabled: row.original.is_expired,
              disabledReason: row.original.is_expired
                ? 'Reauthenticate before syncing'
                : undefined,
              onClick: () => syncEmailIntegration(row.original.id),
            },
            createDeleteAction(() => handleRowDelete(row.index)),
          ]
          return (
            <ActionsDropdown
              className={isPlain ? '!w-full justify-center' : undefined}
              actions={actions}
            />
          )
        },
      },
    ],
    [isPlain, filteredResults]
  )

  return (
    <>
      <DataTableContainer className="h-full">
        <DataTable
          tableKey={tableKey}
          tableHeader={
            <div
              data-table-toolbar
              className="flex flex-row items-center justify-between gap-3 px-4 py-3"
            >
              <div className="relative w-full max-w-md">
                <PiMagnifyingGlass
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search emails..."
                  className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                />
              </div>
              <FbButton
                variant="primary"
                size="sm"
                onClick={() => openModal()}
                className="!bg-brand-bright !text-white hover:!bg-brand-active shrink-0"
              >
                <PiPlus size={16} />
                Add Email
              </FbButton>
            </div>
          }
          data={filteredResults}
          columns={columns}
          sortableFields={sortableFields}
          isPaginationEnabled={!searchQuery}
          paginationOptions={{
            totalRows: searchQuery
              ? filteredResults.length
              : (data?.count ?? 0),
            pageSize: PAGE_SIZE,
            setPage: setPage,
            page: page,
            isPaginationLoading: isFetching,
          }}
          loading={isFetching}
          stickyLastColumn={isPlain}
          enableColumnResizing
          rowHeight={44}
          headerHeight={40}
        />
      </DataTableContainer>
      <Modal
        open={showModal}
        title={selectedEmailID ? 'Edit Email Visibility' : 'Add Email'}
        description={
          selectedEmailID
            ? ''
            : 'Select your email provider to add an account to First Bite, then choose between private or team visibility. You can always change visibility preferences later.'
        }
        onOpenChange={(open) => !open && hideModal()}
        onAccept={selectedEmailID ? onSubmitUpdate : undefined}
        acceptButtonText={selectedEmailID ? 'Update' : 'Create'}
        hideCancelButton={!selectedEmailID}
      >
        {selectedEmailID ? (
          <input
            name="email"
            type="text"
            placeholder="Email"
            disabled
            value={email}
            readOnly
            className="w-full h-11 px-4 text-sm bg-gray-50 border border-gray-300 rounded-lg text-gray-500"
          />
        ) : (
          <div className="flex flex-col gap-2 items-center mb-4">
            <GoogleButton
              className="w-min"
              onClick={() => onContinueButtonClick('google')}
            />
            <OutlookButton
              className="w-min"
              onClick={() => onContinueButtonClick('microsoft')}
            />
          </div>
        )}

        <div className="flex items-center">
          <Checkbox
            id="is_shared"
            onChange={(e) => setIsShared(e.target.checked)}
            value={isShared ? 'true' : 'false'}
            checked={isShared ?? true}
          />
          <label htmlFor="is_shared" className="ml-2 text-sm text-gray-700">
            Share email visibility with my team
          </label>
        </div>
      </Modal>
      <Modal
        open={showSuccessModal}
        title={'Success'}
        description="Email integrated successfully"
        onOpenChange={(open) => !open && setShowSuccessModal(false)}
        onAccept={() => setShowSuccessModal(false)}
        acceptButtonText={'Close'}
        hideCancelButton
      />
    </>
  )
}
